import { useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import {
  classifyFlowWriteFailure,
  useFlowSettingsQuery,
  useReplaceFlowSettingsMutation,
  type FlowWriteFailure,
  type ProjectFlowSettings,
} from "@/api/flowSettings";
import { queryKeys, type ClinicalSummaryItem } from "@/api/hooks";
import type { LibraryAsset } from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import {
  alwaysTracks,
  autoSlotCount,
  maxSlotCount,
  priorityWrites,
  resizeSlots,
  selectionByAssetId,
  slotDraftOf,
  slotEditBase,
  slotWrites,
  servedLimit,
  stagedKey,
  stagedWrites,
  toggleAlways,
  type SlotDraft,
  type StagedChange,
} from "@/lib/flowSettings";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import type { SectionReviewGrouping } from "@/ui/review/SectionReviewList";

import { RoutingDisclosure } from "./PathwayRouting";

/**
 * The Project Pathways flow surface (#855), over #854's typed contract.
 *
 * ## WHAT IT IS, AND WHAT IT REFUSES TO BECOME
 *
 * The [Lovable Pathways
 * screen](https://noproductcontent.lovable.app/projects/mod/config/pathways) is
 * two named groups of compact adopted rows — `ALWAYS ASSESSED` and `DYNAMIC —
 * ASSESSED BASED ON MAIN CONCERN` — each row carrying an `Always assessed`
 * switch, with a `Max dynamic pathways per patient` field inside the Dynamic
 * group and one top-right `Adopt from library`. That arrangement is preserved
 * exactly. What #855 ADDS is the compact routing-order/priority disclosure
 * below the groups, because Lovable shows a selection with no order and no
 * fallback, and the pinned loader routes on both.
 *
 * It is deliberately NOT a second Pathways screen: the rows are
 * `SectionReview`'s own, the adoption action is the pane's own, and the
 * grouping is derived from the SERVICE's live selection
 * (`ProjectFlowSettings.adoptedPathways`) rather than recomputed here. That is
 * the contract's own reason for publishing that list — "the Always/Dynamic
 * groups a client renders come from this list, which is derived from the same
 * effective document as `selection`, so no screen has to choose between two
 * answers".
 *
 * ## WHAT THE SWITCH ACTUALLY WRITES
 *
 * There is no Always/Dynamic mode field on the wire, on purpose: selection is
 * DERIVED from `dynamic_track_slots`. So the switch edits the SLOT LIST — on
 * takes the first automatic slot and pins this Pathway's track, off returns
 * that slot to `auto` — which is what keeps the dynamic limit fixed while the
 * selection moves. For the MOD/NII source recipe that is `[ptsd, auto, auto]`:
 * three slots, one held by an always-assessed Pathway, two left for
 * classification. Three AUTOMATIC slots is a different configuration and this
 * surface never produces one by accident: the disclosure below the groups
 * numbers every slot and says which kind it is.
 *
 * ## THE MAXIMUM IS THE SLOT COUNT, AND NOTHING HERE IS A CONSTANT
 *
 * `maxDynamicTracks` is the obsolete spelling of the limit; in the slots shape
 * the loader derives it from the slot list. So the field edits the list, the
 * obsolete key gets Remove / Restore inherited actions of its own, and the
 * upper bound offered comes from the response's own `fillableTrackCount` — the
 * pinned loader's rule, not a product rule and not a literal three. The
 * registry's separate maximum-adopted-Dynamic-Pathway count still bounds the
 * CATALOG and is reported by `PathwaySelectionSummary`; the two are different
 * questions and this surface does not conflate them.
 */

export type PathwayFlowSurface = {
  readonly projectId: string;
  readonly query: UseQueryResult<ProjectFlowSettings>;
  /**
   * The reading this surface renders, or `null` when there is none yet.
   *
   * May be a RETAINED reading whose latest refetch failed — see `settled`,
   * which is what write controls are gated on.
   */
  readonly state: ProjectFlowSettings | null;
  /**
   * What the reading on screen currently IS.
   *
   * Three states, because TanStack reports two of them the same way. It keeps
   * `data` while a refetch runs AND after one fails, and `status` stays
   * `success` throughout the first — so `isSuccess` alone answers "has this
   * query ever succeeded", not "is what I am showing current", which is the
   * question a write has to ask. Measured against the installed
   * `@tanstack/query-core` 5.101.4, not assumed.
   *
   *   `current`     the reading is the service's latest answer
   *   `refreshing`  a re-read is in flight, so this reading is already
   *                 superseded — the window an adoption opens, since an
   *                 adoption invalidates this key
   *   `paused`      a re-read was started and is PARKED (offline, or a held
   *                 retry). Superseded like `refreshing`, but it does not
   *                 resolve on its own and a bare `refetch()` returns the
   *                 parked retryer's promise without sending anything, so this
   *                 state carries the cancel-then-refetch recovery instead
   *   `behind`      the re-read failed; the reading is retained
   *
   * FOUR, not three, because `fetchStatus` distinguishes fetching from paused
   * and `status` reports neither: a parked refetch is `isSuccess: true,
   * isFetching: false`, which round 3 classified as `current` and offered
   * writes against indefinitely. Derived from `fetchStatus` first for that
   * reason.
   *
   * The reading is SHOWN in all four — blanking a section over a transient
   * outage is worse — but a write is offered only in the first, and the others
   * say which they are. `refetchOnWindowFocus` is off for this client, so
   * `refreshing` is not a background flicker: it means something invalidated
   * this read.
   */
  readonly readState: "current" | "refreshing" | "paused" | "behind";
  /** `readState === "current"`. The one gate every write control reads. */
  readonly settled: boolean;
  /**
   * Re-read this Project's flow settings.
   *
   * `cancelQueries` then `refetchQueries`, never a bare `refetch()`: a PAUSED
   * query answers `refetch()` with the parked retryer's promise without firing
   * anything — which is exactly the state this recovery is offered in.
   */
  readonly retryRead: () => void;
  readonly slots: readonly SlotDraft[];
  /**
   * The dynamic limit to DISPLAY: the pending slot count while editing, and
   * otherwise the service's own `maxDynamicTracks` for whichever shape the
   * Project is in. Null when the service could not resolve one.
   */
  readonly displayLimit: number | null;
  /** Whether the served document is still in the replaced selection shape. */
  readonly legacyShape: boolean;
  /**
   * What the dynamic-limit box currently holds, or `null` when it is showing
   * the derived value.
   *
   * HELD HERE RATHER THAN IN THE FIELD, because whether it has been APPLIED is
   * a fact the save has to know. Text that `resize` refused — a value above the
   * pack's bound, a zero, an emptied box — leaves the slot list untouched while
   * the box goes on showing it, and an unrelated edit would then carry a save
   * whose result contradicts what is on screen. `saveRefusal` covers it.
   */
  readonly limitText: string | null;
  readonly editLimit: (raw: string) => void;
  /**
   * A stale refusal stands, and no further save may be attempted until the
   * author discards or reloads. See `submit`.
   */
  readonly staleBlocked: boolean;
  /**
   * Why a save cannot be made right now, or `null` when one can.
   *
   * ONE RULE, read by every control that could start a write and re-checked
   * inside `submit` itself. The confirmation dialog is the reason it exists:
   * it is opened from a state that permits a save and can outlive it — a
   * refetch fails, or a concurrent write lands — and a dialog carrying its own
   * `disabled` expression would have gone on submitting after the bar behind it
   * had stopped.
   */
  readonly saveRefusal: string | null;
  readonly priorities: ReadonlyMap<string, string>;
  /** Removals and restores staged for the next save, by their address. */
  readonly staged: ReadonlyMap<string, StagedChange>;
  readonly dirty: boolean;
  readonly canWrite: boolean;
  readonly writeReason: string | null;
  readonly saving: boolean;
  /** A change this surface refused before a round trip, with the remedy. */
  readonly localRefusal: string | null;
  readonly priorityRefusals: ReadonlyMap<string, string>;
  readonly failure: FlowWriteFailure | null;
  readonly savedAt: number | null;
  /** Whether the Save confirmation is open. */
  readonly confirming: boolean;
  readonly toggleAlways: (track: string, on: boolean) => void;
  readonly resize: (count: number) => void;
  readonly editPriority: (track: string, text: string) => void;
  readonly stage: (change: StagedChange) => void;
  readonly askToSave: () => void;
  readonly cancelSave: () => void;
  readonly discard: () => void;
  readonly save: () => void;
};

/**
 * The flow read, the pending edits and the one write, held once.
 *
 * HELD BY `SectionDiagnostics` and passed down, on the same one-read discipline
 * the rest of that screen follows: the rows' grouping and the controls above
 * them both describe this state, and two cache observers of one key are two
 * things that can disagree in front of a clinician.
 *
 * `enabled` is false on the five sections that have no flow control, so they
 * issue no request at all — a hook may not be called conditionally, and a
 * disabled query is the supported way to say "not this section".
 *
 * ## ONE REQUEST, BECAUSE THE LOADER JUDGES A DOCUMENT
 *
 * Every pending change — the slot list, each track's priority, each staged
 * removal or restore — goes in ONE `PUT`. That is not batching for its own
 * sake: the service validates the DOCUMENT this write would leave, and a
 * default Project sits in the loader's legacy selection shape, where authoring
 * slots without removing `default_tracks` and `max_dynamic_tracks` declares two
 * shapes at once and removing either without the slots declares neither. Both
 * are refused. Writing the three together is the only path there is, and the
 * service accepts it in one revisioned commit.
 */
export function usePathwayFlow(
  projectId: string,
  { enabled }: { enabled: boolean },
): PathwayFlowSurface {
  const query = useFlowSettingsQuery(projectId, { enabled });
  const mutation = useReplaceFlowSettingsMutation(projectId);
  const write = useCapability("draft.write");
  const retryRead = useRetryPausedQuery(queryKeys.projectFlow(projectId));

  // NULL UNTIL THE FIRST EDIT, so a refetch that brings a newer document
  // through is adopted by a surface nobody is editing, and is NOT adopted over
  // entered text. Once the author has touched the form, what they typed stays
  // on screen through every refetch and through every refusal — the ticket's
  // "cancellation, stale save and invalid content preserve entered text".
  const [slotDraft, setSlotDraft] = useState<SlotDraft[] | null>(null);
  const [priorityDraft, setPriorityDraft] = useState<Map<string, string>>(
    () => new Map(),
  );
  const [staged, setStaged] = useState<Map<string, StagedChange>>(
    () => new Map(),
  );
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);
  const [limitText, setLimitText] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // THE REVISION THIS EDIT IS BOUND TO, captured when editing began.
  //
  // Round 1 sent `state.draftVersion` at submit time. A stale refusal refetches
  // the flow read, so the very next Save carried the edits the author had kept
  // — correctly — but bound to the version that refusal had just fetched:
  // the compare-and-set then PASSED and silently overwrote the concurrent
  // change nobody had looked at. The whole point of the check is that a write
  // built on a revision the author never saw is refused, so the version is
  // captured once and never re-read, exactly as `ResponseFilteringControl`
  // captures its CAS token.
  const [editVersion, setEditVersion] = useState<string | null>(null);
  //: A stale refusal is TERMINAL for this edit. Retrying cannot be safe — the
  //: document moved under it — so the only ways on are Discard (drop the edit)
  //: or a reload (see it).
  const [staleBlocked, setStaleBlocked] = useState(false);

  const state = query.data ?? null;
  // See `PathwayFlowSurface.readState`. `fetchStatus` FIRST, because it is the
  // only field that separates a re-read in flight from one that is parked;
  // `status` reports a parked refetch as `success` and `isFetching` as false,
  // which is how round 3's derivation called it `current`.
  const readState: "current" | "refreshing" | "paused" | "behind" =
    query.fetchStatus === "fetching"
      ? "refreshing"
      : query.fetchStatus === "paused"
        ? "paused"
        : query.isSuccess
          ? "current"
          : "behind";
  const settled = readState === "current";
  const served = state === null ? [] : slotDraftOf(state);
  const slots = slotDraft ?? served;
  // What an EDIT starts from, which on the legacy shape is the limit the
  // Project already runs written as automatic slots. Never `slots` itself: that
  // would report a Project nobody has touched as dirty.
  const editBase = slotDraft ?? (state === null ? [] : slotEditBase(state));

  const priorityEntries = state?.trackPriorityEntries ?? [];
  const { writes: priorityChanges, refusals: priorityRefusals } = priorityWrites(
    priorityEntries,
    priorityDraft,
  );
  const slotChanges =
    state === null || staged.has(stagedKey("dynamicTrackSlots", null))
      ? []
      : slotWrites(state, slots);
  const dirty =
    staged.size > 0 ||
    slotChanges.length > 0 ||
    priorityChanges.length > 0 ||
    // A refused priority is dirty too: the field holds text that differs from
    // the served value, and calling it clean would let the author navigate away
    // believing the number they typed had been kept.
    priorityRefusals.size > 0;

  function beginEdit() {
    setSavedAt(null);
    if (state !== null) {
      setEditVersion((current) => current ?? state.draftVersion);
    }
  }

  function changeSlots(next: { slots: SlotDraft[]; refusal: string | null }) {
    setLocalRefusal(next.refusal);
    if (next.refusal !== null) {
      return;
    }
    setSlotDraft(next.slots);
    beginEdit();
    // EDITING AND STAGING ARE MUTUALLY EXCLUSIVE at one address: a staged
    // removal beside an edited value is two ops for one setting, which the
    // request refuses outright rather than resolving by order.
    setStaged((current) => without(current, stagedKey("dynamicTrackSlots", null)));
  }

  function clear() {
    setSlotDraft(null);
    setPriorityDraft(new Map());
    setStaged(new Map());
    setLocalRefusal(null);
    setLimitText(null);
    setConfirming(false);
    setEditVersion(null);
    setStaleBlocked(false);
  }

  // WHAT THE LIMIT BOX SHOWS versus what the slot list actually is. Anything
  // `resize` refused leaves the two apart, and a save while they are apart
  // writes a document that contradicts the screen.
  const displayedLimit =
    slotDraft !== null
      ? slotDraft.length
      : state === null
        ? null
        : servedLimit(state);
  // COMPARED AS A NUMBER, not as a string. `02`, `2.0` and `2e0` are all
  // valid contents of a number input and all parse to the value `resize`
  // ACCEPTED — a string compare called each of them unapplied and refused a
  // save that had in fact been applied. An empty box is still unapplied
  // (`Number("")` is 0, which would otherwise read as an accepted zero), and so
  // is anything that does not parse.
  const typedLimit = limitText === null ? null : Number(limitText);
  const limitApplied =
    limitText === null ||
    (limitText.trim() !== "" &&
      typedLimit !== null &&
      Number.isFinite(typedLimit) &&
      displayedLimit !== null &&
      typedLimit === displayedLimit);

  const saveRefusal = !write.allowed
    ? (write.reason ??
      "This account may not change this project's dynamic phase settings.")
    : readState === "refreshing"
      ? "This project's dynamic phase is being re-read. Saving waits for that to land, so a change is never written against a reading the Editor Service has already replaced."
      : readState === "paused"
        ? "A re-read of this project's dynamic phase is waiting for the connection. Until it completes, the selection here is the previous reading and nothing can be saved against it."
        : readState === "behind"
          ? "This project's dynamic phase could not be re-read, so nothing here can be saved against it. Read it again first."
          : staleBlocked
          ? "This project's draft changed since this screen read it. Saving stays refused until you reload the section or discard."
          : priorityRefusals.size > 0
            ? "One of the priorities below is not a whole number. Correct it, or restore it to the base pack's value."
            : !limitApplied
              ? "The maximum shown above has not been applied — it was refused, or the box is empty. Correct it, or put it back to the value in effect, before saving."
              : null;

  return {
    projectId,
    query,
    state,
    readState,
    settled,
    retryRead,
    saveRefusal,
    slots,
    displayLimit: displayedLimit,
    limitText,
    editLimit(raw) {
      setLimitText(raw);
      const next = Number(raw);
      if (raw === "" || !Number.isInteger(next)) {
        // NOT APPLIED, and not silently dropped either: the box keeps what was
        // typed so it can be corrected, and `saveRefusal` holds the save.
        setLocalRefusal(null);
        return;
      }
      changeSlots(
        resizeSlots(editBase, next, state === null ? null : maxSlotCount(state)),
      );
    },
    legacyShape: state?.selection.shape === "legacy",
    staleBlocked,
    priorities: priorityDraft,
    staged,
    dirty,
    canWrite: write.allowed,
    writeReason: write.reason,
    saving: mutation.isPending,
    localRefusal,
    priorityRefusals,
    failure: mutation.isError ? classifyFlowWriteFailure(mutation.error) : null,
    savedAt,
    confirming,
    toggleAlways(track, on) {
      changeSlots(toggleAlways(editBase, track, on));
    },
    resize(count) {
      // The loader's own upper bound, from the response, applied here rather
      // than left to the field's `max` attribute — which a typed value ignores.
      changeSlots(
        resizeSlots(editBase, count, state === null ? null : maxSlotCount(state)),
      );
    },
    editPriority(track, text) {
      setLocalRefusal(null);
      beginEdit();
      setPriorityDraft((current) => new Map(current).set(track, text));
      setStaged((current) => without(current, stagedKey("trackPriority", track)));
    },
    stage(change) {
      const key = stagedKey(change.setting, change.track);
      setLocalRefusal(null);
      beginEdit();
      setStaged((current) => {
        const next = new Map(current);
        // A SECOND PRESS UNDOES IT. The control is the only place this decision
        // exists before a save, so it has to be reversible without a Discard
        // that would take every other pending edit with it.
        if (next.get(key)?.op === change.op) {
          next.delete(key);
        } else {
          next.set(key, change);
        }
        return next;
      });
      if (change.setting === "dynamicTrackSlots") {
        setSlotDraft(null);
        // AND THE BOX WITH IT. Staging a reset drops the pending slot list, so
        // the limit reverts to the served one; text left over from the edit
        // being discarded then read as "not applied" and blocked the very save
        // that carries the reset.
        setLimitText(null);
      }
      const track = change.track;
      if (change.setting === "trackPriority" && track !== null) {
        setPriorityDraft((current) => without(current, track));
      }
    },
    askToSave() {
      if (state === null || saveRefusal !== null) {
        return;
      }
      // CONFIRMED ONLY WHERE SOMETHING IS DISCARDED. A slot or priority edit is
      // an ordinary revisioned write and needs no ceremony; a removal takes a
      // key out of the document the pinned engine loads and a restore drops
      // this Project's own value, and both are named before they happen.
      if (staged.size === 0) {
        void submit();
        return;
      }
      setConfirming(true);
    },
    cancelSave() {
      // EVERY PENDING CHANGE SURVIVES. Cancelling the confirmation is not
      // Discard: nothing is written and nothing entered is lost, which is the
      // ticket's own criterion.
      setConfirming(false);
    },
    discard() {
      clear();
      setSavedAt(null);
      mutation.reset();
    },
    save: submit,
  };

  function submit() {
    // RE-CHECKED HERE, not only where the write was offered. The confirmation
    // dialog opens from a savable state and can outlive it: a refetch fails
    // under it, or a concurrent write lands, and the press that follows would
    // otherwise be computed from a reading this surface has already stopped
    // trusting.
    if (state === null || saveRefusal !== null) {
      return;
    }
    const settings = [
      ...stagedWrites(staged),
      ...slotChanges,
      ...priorityChanges,
    ];
    if (settings.length === 0) {
      return;
    }
    mutation.mutate(
      // THE CAPTURED REVISION, not whatever the last refetch left behind.
      { expectedDraftVersion: editVersion ?? state.draftVersion, settings },
      {
        onSuccess() {
          // The RESPONSE is now the served state, so the local draft is
          // dropped and the surface reads back what the server derived — which
          // is the effective-state readback this ticket asks for, not an echo
          // of what was typed.
          clear();
          setSavedAt(Date.now());
        },
        onError(error) {
          // The confirmation closes; the pending changes do not. A refusal is
          // something to read and act on, not a reason to lose the work.
          setConfirming(false);
          // A STALE REFUSAL ENDS THIS EDIT. Everything entered is kept so the
          // author can copy it forward, and Save stays refused until they
          // discard or reload — because the only thing a second press could do
          // is rebase their edit onto a document they have not seen.
          if (classifyFlowWriteFailure(error).kind === "stale") {
            setStaleBlocked(true);
          }
        },
      },
    );
  }
}

function without<T>(map: ReadonlyMap<string, T>, key: string): Map<string, T> {
  const next = new Map(map);
  next.delete(key);
  return next;
}

/**
 * The `controls`-slot content, or `undefined` when there is nothing to put
 * there.
 *
 * A FUNCTION rather than a component, and that is the whole reason it exists in
 * this shape: `ProjectSectionPane` renders a `space-y-3` wrapper for whatever it
 * is handed, so a component that renders `null` still opens a blank gap above
 * the rows — the defect #909 round 2 fixed, and the reason that pane's own note
 * says the caller must ask first. The dynamic-phase groups and the limit field
 * are the ROWS' own (they arrive through `grouping`), so on a clean, settled
 * Project this slot legitimately holds nothing at all.
 *
 * What it does hold is what an author must read BEFORE the rows: a finding the
 * pinned loader would refuse, a change this surface refused, a write the service
 * refused, and the pending-change bar. Above the rows because `ProjectSectionPane`
 * puts what configures the rows ahead of them, and because a refusal an author
 * has to scroll past the list to find is a hidden refusal.
 */
export function pathwayFlowControls(
  surface: PathwayFlowSurface,
): ReactNode | undefined {
  const notices = <PathwayFlowNotices surface={surface} />;
  const hasSomethingToSay =
    surface.state === null ||
    surface.readState !== "current" ||
    surface.state.findings.length > 0 ||
    surface.localRefusal !== null ||
    surface.failure !== null ||
    !surface.canWrite ||
    surface.dirty ||
    surface.savedAt !== null ||
    surface.confirming;
  return hasSomethingToSay ? notices : undefined;
}

function PathwayFlowNotices({ surface }: { surface: PathwayFlowSurface }) {
  const { query, state } = surface;

  if (state === null) {
    // NO READING AT ALL — the FIRST read has not succeeded. Round 4 gave the
    // retained-reading states a recovery and left this one with none, so a
    // first read that failed or parked was a dead end until the route was left
    // and re-entered. `isPending` is the only arm with nothing to offer: it is
    // still in flight.
    const reading = query.isPending && query.fetchStatus === "fetching";
    return (
      <div
        data-slot="pathway-flow"
        className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
      >
        <p className="min-w-0 flex-1" role={reading ? "status" : "alert"}>
          {reading
            ? "Reading how this project routes its dynamic phase…"
            : query.fetchStatus === "paused"
              ? "Reading this project's dynamic phase is waiting for the connection. The pathways below are listed without their runtime selection; adoption is unaffected."
              : "This project's dynamic phase settings could not be read, so the pathways below are listed without their runtime selection. Adoption is unaffected."}
        </p>
        {reading ? null : (
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-slot="pathway-flow" className="space-y-3">
      {surface.readState === "refreshing" ? (
        // A RE-READ IS IN FLIGHT, so what is below is already superseded. This
        // is the window an adoption opens — it invalidates this key — and
        // `isSuccess` stays true right through it, which is why gating on that
        // alone left every control writable here. Announced politely rather
        // than as an alert: nothing is wrong, and it resolves on its own.
        <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
          Re-reading this project&apos;s dynamic phase. The selection below is
          the previous reading until that lands, so changes wait for it.
        </p>
      ) : surface.readState === "paused" ? (
        // PARKED, NOT FAILED, and it will not resolve on its own. The recovery
        // is `cancelQueries` then `refetchQueries`: a bare `refetch()` on a
        // paused query answers with the parked retryer's own promise without
        // sending anything, which is the button that does nothing
        // `useRetryPausedQuery` exists to avoid.
        <Notice tone="warning" role="alert">
          <span>
            A re-read of this project&apos;s dynamic phase is waiting for the
            connection, so the selection below is the previous reading. Nothing
            here can be changed until it completes.
          </span>
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        </Notice>
      ) : surface.readState === "behind" ? (
        // THE READING IS RETAINED, AND SAID TO BE. TanStack keeps `data` when a
        // refetch fails, so without this the groups below would keep describing
        // the selection as it was with nothing on screen admitting it. The rows
        // stay: blanking a section over a transient outage is worse than
        // labelling it. The write controls do not.
        <Notice tone="warning" role="alert">
          <span>
            This project&apos;s dynamic phase could not be re-read, so the
            selection below is the last reading that succeeded and may be behind
            what the Editor Service now holds. Nothing here can be changed until
            it can be read again.
          </span>
          <Button size="sm" variant="outline" onClick={surface.retryRead}>
            Read it again
          </Button>
        </Notice>
      ) : null}

      {state.findings.length > 0 ? (
        <Notice tone="destructive" role="alert">
          <span>
            The pinned engine would not load this project&apos;s dynamic phase
            as it stands. {state.findings[0]?.message} A freeze is refused until
            it is resolved.
            {state.findings.length > 1
              ? ` ${state.findings.length - 1} further ${
                  state.findings.length === 2 ? "reason is" : "reasons are"
                } reported below the pathways.`
              : ""}
          </span>
        </Notice>
      ) : null}

      {surface.localRefusal !== null ? (
        <Notice tone="warning" role="alert">
          <span>{surface.localRefusal}</span>
        </Notice>
      ) : null}

      {surface.failure !== null ? (
        <FlowFailureNotice failure={surface.failure} />
      ) : null}

      {!surface.canWrite ? (
        <p className="text-xs text-muted-foreground">
          {surface.writeReason ??
            "This account may read this project's dynamic phase settings but not change them."}
        </p>
      ) : null}

      {surface.dirty ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-card px-4 py-3">
          <p className="min-w-0 flex-1 text-sm text-foreground">
            Unsaved changes to the dynamic phase. Nothing is written until you
            save, and everything below is saved together as one revision.
          </p>
          {surface.saveRefusal !== null ? (
            // A DISABLED SAVE MUST SAY WHY. The reason was rendered only inside
            // the confirmation, which a refused save never reaches — so the
            // button simply stopped working with nothing on screen accounting
            // for it.
            <p
              role="alert"
              className="basis-full text-xs text-muted-foreground"
            >
              {surface.saveRefusal}
            </p>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            onClick={surface.discard}
            disabled={surface.saving}
          >
            Discard
          </Button>
          <Button
            size="sm"
            onClick={surface.askToSave}
            disabled={surface.saving || surface.saveRefusal !== null}
          >
            {surface.saving ? "Saving…" : "Save"}
          </Button>
        </div>
      ) : surface.savedAt !== null ? (
        <p role="status" className="text-xs text-muted-foreground">
          Saved. The selection below is the Editor Service&apos;s own reading of
          the document this project now stores.
        </p>
      ) : null}

      {surface.confirming ? <SaveConfirmDialog surface={surface} /> : null}
    </div>
  );
}

/**
 * The classified write failure, in the caller's words for a state the caller
 * can act on and in the SERVER's for a refusal only it can explain.
 */
function FlowFailureNotice({ failure }: { failure: FlowWriteFailure }) {
  const text = ((): string => {
    switch (failure.kind) {
      case "stale":
        return "This project's draft changed since this screen read it, so nothing was written. What you entered is kept, and saving stays refused: applying it now would write it over a change nobody here has seen. Reload the section to see the current settings, then re-enter it — or discard.";
      case "conflict":
        return `${failure.message} Nothing was written and what you entered is kept.`;
      case "upstream":
        return "The Editor Service could not reach the pinned clinical engine, so it could not check this change against the loader. Nothing was written. This one is worth trying again.";
      case "denied":
        return "This account may not change this project's dynamic phase settings. Nothing was written.";
      case "invalid":
        // THE SERVER'S SENTENCE ALONE. `ErrorDetail.field` is a dotted request
        // address, and #488's amendment bans a dotted address on a Project
        // route; the message is already field-addressed in words, which is the
        // half an author can act on.
        return `${failure.message} Nothing was written and what you entered is kept.`;
      case "ambiguous":
        return "The Editor Service did not answer this write, so it is not known whether it was applied. Nothing here has been resent. Reload the section before trying again.";
      case "error":
        return `The Editor Service refused this write (${failure.code}). Nothing was written and what you entered is kept.`;
    }
  })();
  return (
    <Notice tone="destructive" role="alert">
      <span>{text}</span>
    </Notice>
  );
}

/**
 * The one confirmation, and it names what is discarded.
 *
 * Shown only when the pending set contains a removal or a restore. Cancel
 * writes nothing and keeps every pending change, which is what makes a
 * cancelled save cost the author nothing.
 */
function SaveConfirmDialog({ surface }: { surface: PathwayFlowSurface }) {
  const removals = [...surface.staged.values()].filter(
    (change) => change.op === "delete",
  );
  const restores = [...surface.staged.values()].filter(
    (change) => change.op === "reset",
  );
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !surface.saving) {
          surface.cancelSave();
        }
      }}
    >
      <DialogContent
        busy={surface.saving}
        title="Save the dynamic phase"
        description="One revision, checked against the pinned engine's own loader before anything is stored."
        footer={
          <>
            <Button
              variant="outline"
              onClick={surface.cancelSave}
              disabled={surface.saving}
            >
              Cancel
            </Button>
            <Button
              onClick={surface.save}
              disabled={surface.saving || surface.saveRefusal !== null}
            >
              {surface.saving ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          {removals.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">
                Removed from the document the pinned engine loads
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                {removals.map((change) => (
                  <li key={`${change.setting}:${change.track ?? ""}`}>
                    {change.subject}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                Removed is not set to nothing: the loader will not see the key
                at all.
              </p>
            </div>
          ) : null}

          {restores.length > 0 ? (
            <div>
              <p className="font-medium text-foreground">
                Restored to the base pack&apos;s value
              </p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-muted-foreground">
                {restores.map((change) => (
                  <li key={`${change.setting}:${change.track ?? ""}`}>
                    {change.subject}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                This project&apos;s own value is dropped, and the base pack
                supplies the setting again.
              </p>
            </div>
          ) : null}

          {surface.saveRefusal !== null ? (
            // THE STATE CHANGED UNDER THIS DIALOG. It was opened from a savable
            // surface; a failed refetch or a concurrent write can end that
            // while it stands, and a Save that had merely gone quiet would look
            // like a slow request rather than a refusal.
            <Notice tone="warning" role="alert">
              <span>{surface.saveRefusal}</span>
            </Notice>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Cancelling writes nothing and keeps every change on this screen.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The grouped-row model for the Pathways section, or `undefined` where it
 * cannot be built.
 *
 * `undefined` — a flat list — is the honest answer while the flow read has not
 * settled or has failed: grouping rows into `Always assessed` and `Dynamic`
 * without the selection would be this browser guessing at a runtime fact. The
 * rows, their adoption controls and the section's Details are unaffected, which
 * is why the failure is a line above the list rather than a state instead of it.
 */
export function pathwayFlowGrouping({
  surface,
  catalog,
  projectId,
}: {
  surface: PathwayFlowSurface;
  /** The caller's catalog read, for clinical names. `null` where unread. */
  catalog: readonly LibraryAsset[] | null;
  projectId: string;
}): SectionReviewGrouping | undefined {
  const state = surface.state;
  if (state === null) {
    return undefined;
  }

  const selection = selectionByAssetId(state);
  const nameById = new Map(
    (catalog ?? []).map((asset) => [asset.id, asset.name] as const),
  );
  // TRACK → the clinical name of the Pathway adopted on it. Falls back to the
  // track's own registered name where the catalog has not answered, which is
  // honest rather than blank: the engine key IS what the document carries, and
  // an author who sees it can still act on it.
  const trackNames = new Map<string, string>();
  for (const pin of state.adoptedPathways) {
    const name = nameById.get(pin.assetId);
    if (name !== undefined && !trackNames.has(pin.track)) {
      trackNames.set(pin.track, name);
    }
  }
  const nameOfTrack = (track: string): string => trackNames.get(track) ?? track;

  const alwaysCount = state.adoptedPathways.filter(
    (pin) => pin.mode === "always",
  ).length;
  const dynamicCount = state.adoptedPathways.length - alwaysCount;
  // WHY A GROUP IS EMPTY, which is two different facts. A Project that adopts
  // no Pathway at all has empty groups because it has adopted nothing; a
  // Project that adopts three and pins none has an empty Always group because
  // of how its slots are configured. Saying the second where the first is true
  // would describe a selection the Project has not made.
  const adoptsNone = state.adoptedPathways.length === 0;
  const NOTHING_ADOPTED =
    "This project has not adopted a pathway yet. Adopt one from the shared clinical library to place it in the dynamic phase.";

  return {
    ungroupedLabel: "No runtime selection",
    ungroupedDescription:
      "The Editor Service reports no dynamic-phase selection for these pins: their registered coordinate is not reachable in this project's conversation, so they hold no position in the routing below. They stay listed here with their own actions.",
    summary: (
      <p className="text-sm text-muted-foreground">
        {`${state.adoptedPathways.length} ${
          state.adoptedPathways.length === 1 ? "pathway" : "pathways"
        } · ${alwaysCount} always assessed, ${dynamicCount} dynamic`}
      </p>
    ),
    groups: [
      {
        key: "always",
        label: "Always assessed",
        description: "Runs for every patient, no matter their main concern.",
        empty: adoptsNone
          ? NOTHING_ADOPTED
          : "No pathway is assessed for every patient. Every dynamic phase slot is filled by classification.",
      },
      {
        key: "dynamic",
        label: "Dynamic — assessed based on main concern",
        lead: <DynamicLimitRow surface={surface} state={state} />,
        empty: adoptsNone
          ? NOTHING_ADOPTED
          : "Every adopted pathway in this project is assessed for every patient.",
      },
    ],
    groupKeyOf(item) {
      const pin = selection.get(item.id);
      if (pin === undefined) {
        return null;
      }
      return pin.mode === "always" ? "always" : "dynamic";
    },
    controlOf(item) {
      const pin = selection.get(item.id);
      if (pin === undefined) {
        return null;
      }
      // ONLY THE TRACK'S FAMILY DEFAULT CARRIES THE SWITCH.
      //
      // The service's `always` is two facts: a dynamic slot pins the track AND
      // this pin is that track's family default — `assetType` says which,
      // `pathway` for the default and `pathway_variant` for a variant. Round 1
      // derived the switch from the track alone, so a variant adopted on an
      // Always track rendered CHECKED while the service kept it in the Dynamic
      // group, and toggling it moved the FAMILY DEFAULT's slot: off released a
      // selection this row does not hold, on promoted a Pathway this row does
      // not name. A control that writes somebody else's decision is worse than
      // no control, so the variant row says whose decision it follows instead.
      if (pin.assetType !== "pathway") {
        return <VariantSelectionNote track={pin.track} />;
      }
      return (
        <AlwaysSwitch
          surface={surface}
          item={item}
          track={pin.track}
          servedAlways={pin.mode === "always"}
        />
      );
    },
    footer: (
      <RoutingDisclosure
        surface={surface}
        state={state}
        nameOfTrack={nameOfTrack}
        projectId={projectId}
      />
    ),
  };
}

/**
 * One row's `Always assessed` switch — Lovable's control, in Lovable's place.
 *
 * `role="switch"` with `aria-checked` rather than a checkbox: the reference
 * renders a toggle whose two states are "assessed for every patient" and "not",
 * and the accessible name carries the Pathway so a control listing of a
 * five-row section is not five controls called "Always assessed".
 *
 * It edits the pending slot draft, never the server: the write is the section's
 * one Save, so an author can mark a Pathway always assessed, adjust the maximum
 * and save both as one revisioned document — which is also the only way the
 * two can be validated together by the loader.
 */
function AlwaysSwitch({
  surface,
  item,
  track,
  servedAlways,
}: {
  surface: PathwayFlowSurface;
  item: ClinicalSummaryItem;
  track: string;
  /** Whether the SERVICE currently reports this pin as always assessed. */
  servedAlways: boolean;
}) {
  // THE PENDING DRAFT, not the served selection: this is what the slot list
  // will BE when saved, and a switch that snapped back to the server's answer
  // until a save landed would read as a control that did nothing.
  const on = alwaysTracks(surface.slots).includes(track);
  // `settled` too: a write offered against a reading whose refresh failed
  // would be computed from a selection the service may already have replaced.
  const blocked = !surface.canWrite || surface.saving || !surface.settled;
  // WHY THE ROW DOES NOT MOVE GROUPS YET. The groups come from the SERVICE's
  // own selection — the one derivation shared with the pin cache — and this
  // browser cannot re-derive it: `always` also requires the pin to be its
  // track's family default, which is server knowledge. Rather than guess, the
  // row says what this switch WILL do to the slot list, in the slot list's own
  // terms, where it cannot be wrong.
  const pending =
    on === servedAlways
      ? null
      : on
        ? "Takes a dynamic slot when saved"
        : "Releases its dynamic slot when saved";
  return (
    <span className="flex items-center gap-2 text-xs text-muted-foreground">
      {pending !== null ? (
        <Badge tone="accent">{pending}</Badge>
      ) : null}
      <span>Always assessed</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-disabled={blocked || undefined}
        aria-label={`${item.name} is always assessed`}
        onClick={() => {
          if (blocked) {
            return;
          }
          surface.toggleAlways(track, !on);
        }}
        className={
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-input bg-secondary transition-colors " +
          "aria-checked:border-primary aria-checked:bg-primary " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring " +
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        }
      >
        <span
          aria-hidden="true"
          className={
            "pointer-events-none ml-0.5 h-3.5 w-3.5 rounded-full bg-card shadow transition-transform " +
            (on ? "translate-x-4" : "translate-x-0")
          }
        />
      </button>
    </span>
  );
}

/**
 * What a VARIANT row says where a family default carries a switch.
 *
 * A variant is assessed when its family's track is, and the slot that decides
 * that belongs to the family default's row. Saying so is the honest half of not
 * offering the control (ADR 0001): the reader learns where the decision lives
 * rather than finding a gap.
 */
function VariantSelectionNote({ track }: { track: string }) {
  return (
    <span className="text-xs text-muted-foreground">
      Follows the {track} pathway&apos;s own row
    </span>
  );
}

/**
 * Lovable's `Max dynamic pathways per patient`, bound to the slot list.
 *
 * The number IS the slot count in the shape the pinned loader reads, so the
 * field grows and shrinks the list rather than writing the obsolete
 * `max_dynamic_tracks` key — which the contract refuses a new value for, and
 * which this project removes through the disclosure below.
 */
function DynamicLimitRow({
  surface,
  state,
}: {
  surface: PathwayFlowSurface;
  state: ProjectFlowSettings;
}) {
  const bound = maxSlotCount(state);
  const automatic = autoSlotCount(surface.slots);
  const pinned = alwaysTracks(surface.slots).length;
  const editing = surface.displayLimit !== null && surface.slots.length > 0;
  // THE SERVICE'S OWN LIMIT FOR WHICHEVER SHAPE THIS PROJECT IS IN.
  //
  // Round 1 rendered `surface.slots.length`, which is 0 on every default
  // Project — those are in the loader's LEGACY shape, where the limit lives in
  // `max_dynamic_tracks` and the slot list does not exist yet. The field
  // therefore showed a confident `0` beside the sentence "this project runs no
  // dynamic phase at all" for a Project running two dynamic tracks per patient.
  const value = surface.displayLimit;
  const blocked = !surface.canWrite || surface.saving || !surface.settled;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border bg-card px-4 py-3">
      <label
        htmlFor="pathway-dynamic-limit"
        className="text-sm font-medium text-foreground"
      >
        Max dynamic pathways per patient
      </label>
      {/*
        THE RAW TEXT LIVES ON THE SURFACE, not in this field.

        It has to be CLEARABLE — a value rendered straight from the derived
        number cannot be emptied, so "2" cleared and retyped as "3" became 23 —
        and whether it has been APPLIED has to be known by the save, because
        text `resize` refused leaves the slot list untouched while the box goes
        on showing it. Holding it here made the first true and the second
        unknowable.
      */}
      <Input
        id="pathway-dynamic-limit"
        type="number"
        inputMode="numeric"
        min={1}
        max={bound}
        value={surface.limitText ?? (value === null ? "" : String(value))}
        aria-describedby="pathway-dynamic-limit-help"
        disabled={blocked}
        onChange={(event) => surface.editLimit(event.target.value)}
        className="h-9 w-20"
      />
      <p
        id="pathway-dynamic-limit-help"
        className="basis-full text-xs text-muted-foreground"
      >
        {value === null
          ? `The Editor Service could not resolve a dynamic limit for this project: its effective document declares neither selection shape completely, which the pinned engine will not load. This pack can fill at most ${bound}.`
          : !editing && surface.legacyShape
            ? // NOT AN ERROR, AND NOT A SLOT LIST EITHER. The Project runs this
              // many dynamic tracks today through the replaced spelling; the
              // conversion happens the moment the author changes anything here,
              // and the disclosure below shows the result before it is saved.
              `${value} dynamic ${
                value === 1 ? "pathway" : "pathways"
              } per patient, from the replaced selection keys this project still inherits. Changing anything here writes the slot list that replaces them, starting from this same limit. This pack can fill at most ${bound}.`
            : pinned === 0
              ? `All ${automatic} of these are chosen by classification. This pack can fill at most ${bound}.`
              : `${pinned} of these ${surface.slots.length} ${
                  pinned === 1
                    ? "slots is taken by a pathway that is"
                    : "slots are taken by pathways that are"
                } always assessed, leaving ${automatic} chosen by classification. This pack can fill at most ${bound}.`}
      </p>
    </div>
  );
}
